import { ReactNode, memo } from 'react';
import { Navigate } from 'react-router';

import { FeatureToggle } from '@fly/shared';
import { useFeatureToggle } from '@fly/shared-hooks';

import { FlyLoader, FlyNotFound } from '../../components';

interface FeatureGuardProps {
  feature: FeatureToggle;
  children: ReactNode;
  redirectTo?: string;
  fallback?: ReactNode;
  showNotFound?: boolean;
}

const FeatureGuardComponent = ({
  feature,
  children,
  redirectTo,
  fallback,
  showNotFound = true,
}: FeatureGuardProps): ReactNode => {
  const { hasFeature, isLoading } = useFeatureToggle();

  if (isLoading) {
    return <FlyLoader />;
  }

  if (!hasFeature(feature)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    if (showNotFound) {
      return <FlyNotFound />;
    }

    return null;
  }

  return <>{children}</>;
};

// Optimization: Only re-render if props change.
// Since useFeatureToggle uses Redux, it will trigger a re-render internally
// only when the feature flags actually change state.
export const FeatureGuard = memo(FeatureGuardComponent);
